import React, { useMemo } from 'react';
import { DataGrid } from '@mui/x-data-grid';
import { Paper, Box, Button, Tooltip, Select, MenuItem } from '@mui/material';
import Chip from '@mui/material/Chip';
import AddCircleOutlineIcon from '@mui/icons-material/AddCircleOutline';
import MoodIcon from '@mui/icons-material/Mood';
import NoteAltOutlinedIcon from '@mui/icons-material/NoteAltOutlined';
import { UpdateCustomerStatusApi } from '../../api/customers/update_customer.ts';
import { updateCustomerRow } from '../../slices/customerSlice.js';
import { useDispatch } from 'react-redux';
import ImageLoading from '../../components/alert/imageLoading.js';

const ageCalculate = (birthDate) => {
    if (!birthDate || typeof birthDate !== 'string') return '-';
    const birth = new Date(birthDate);
    if (isNaN(birth.getTime())) return '-';
    const today = new Date();
    let age = today.getFullYear() - birth.getFullYear();
    if (today.getMonth() < birth.getMonth() || (today.getMonth() === birth.getMonth() && today.getDate() < birth.getDate())) {
        age--;
    }
    return age >= 0 ? age : '-';
};

const statusConfig = {
    "ACEPTADO": { sx: { bgcolor: '#109002', color: '#fff' }, label: 'Aceptado' },
    "NO ACEPTADO": { sx: { bgcolor: '#d32f2f', color: '#fff' }, label: 'No Aceptado' },
    "FALLECIDO": { sx: { bgcolor: '#424242', color: '#fff' }, label: 'Fallecido' },
    "INTERESADO": { sx: { bgcolor: '#ffbf00', color: '#fff' }, label: 'Interesado' },
    "PENDIENTE": { sx: { bgcolor: '#ff6a00', color: '#fff' }, label: 'Pendiente' },
    "NO CONTESTA": { sx: { bgcolor: '#98d1ff', color: '#fff' }, label: 'No Contesta' },
    "APROBADO": { sx: { bgcolor: '#1aff00', color: '#fff' }, label: 'Aprobado' },
    "NO APROBADO": { sx: { bgcolor: '#c30000', color: '#fff' }, label: 'No Aprobado' },
    "ACTIVO": { sx: { bgcolor: '#4CAF50', color: '#fff' }, label: 'Activo' },
    "INACTIVO": { sx: { bgcolor: '#F44336', color: '#fff' }, label: 'Inactivo' },
    "NO INTERESADO": { sx: { bgcolor: '#b30000', color: '#fff' }, label: 'No Interesado' },
    "CONTACTADO": { sx: { bgcolor: '#0051b3', color: '#fff' }, label: 'Contactado' },
};

const StatusCell = ({ params, dispatch }) => {
    const currentStatus = params.value?.toUpperCase();
    const currentConfig = statusConfig[currentStatus] || {};

    const handleChange = async (event) => {
        const newValue = event.target.value;
        const response = await UpdateCustomerStatusApi({
            id_customer: params.row.id_customer,
            status_name: newValue
        });

        if (response) {
            dispatch(updateCustomerRow({
                id_customer: params.row.id_customer,
                status_name: newValue
            }));
        }
    };

    return (
        <Select
            value={currentStatus || ''}
            onChange={handleChange}
            size="small"
            fullWidth
            sx={{
                height: 30, fontSize: '0.75rem', fontWeight: 'bold', borderRadius: '16px',
                bgcolor: currentConfig.sx?.bgcolor || '#ccc',
                color: '#fff',
                '& .MuiOutlinedInput-notchedOutline': { border: 'none' },
                '& .MuiSvgIcon-root': { color: '#fff' },
            }}
        >
            {Object.keys(statusConfig).map((key) => (
                <MenuItem key={key} value={key}>
                    <Chip
                        label={statusConfig[key].label}
                        size="small"
                        sx={{ ...statusConfig[key].sx, width: '100%', cursor: 'pointer' }}
                    />
                </MenuItem>
            ))}
        </Select>
    );
};

const CustomerTable = ({ rows, onOpenCredit, onOpenForm }) => {

    const dispatch = useDispatch();

    const columns = useMemo(() => [
        {
            field: 'updateInfo',
            headerName: 'Actualizar',
            width: 80,
            sortable: false,
            renderCell: (params) => (
                <Tooltip title="Editar información">
                    <Button size="small" onClick={() => onOpenForm(params.row.id_customer, 'updateCustomer')}>
                        <MoodIcon color="info" />
                    </Button>
                </Tooltip>
            )
        },
        {
            field: 'name',
            headerName: 'Nombre',
            minWidth: 250,
            flex: 1,
            valueGetter: (params) => params.toLowerCase(),
            renderCell: (params) => <Box sx={{ textTransform: 'capitalize' }}>{params.value}</Box>
        },
        { field: 'document', headerName: 'Documento', width: 110 },
        {
            field: 'date_birth',
            headerName: 'Edad',
            width: 60,
            renderCell: (params) => ageCalculate(params.value)
        },
        { field: 'name_city', headerName: 'Ciudad', width: 110 },
        { field: 'phone', headerName: 'Teléfono', width: 130 },
        { field: 'phone_alternating', headerName: 'Teléfono Alternativo', width: 130 },
        { field: 'type_customer', headerName: 'Tipo de Cliente', width: 110 },
        { field: 'group', headerName: 'Grupo', width: 100 },
        { field: 'name_campaign', headerName: 'Campaña', width: 100 },
        { field: 'disbursement_office', headerName: 'Pagaduria', width: 130 },
        {
            field: 'name_status',
            headerName: 'Estado Cliente',
            width: 180,
            renderCell: (params) => <StatusCell params={params} dispatch={dispatch} />
        },
        {
            field: 'actions',
            headerName: 'Acciones',
            width: 130,
            sortable: false,
            renderCell: (params) => (
                <Box sx={{ display: 'flex', gap: 0.5 }}>
                    <IconButtonClick color="success" icon={<AddCircleOutlineIcon />} title="Nuevo Crédito" onClick={() => onOpenCredit(params.row.id_customer, 'newCredit')} />
                    <IconButtonClick color="primary" icon={<NoteAltOutlinedIcon />} title="Gestionar" onClick={() => onOpenCredit(params.row.id_customer, 'editCredit')} />
                </Box>
            ),
        },
    ], [onOpenCredit, onOpenForm, dispatch]);

    if (!rows || rows.length === 0) return <ImageLoading />;

    return (
        <Paper elevation={3} sx={{ height: 600, width: '100%', mt: 2, borderRadius: '12px', overflow: 'hidden' }}>
            <DataGrid
                rows={rows}
                columns={columns}
                getRowId={(row) => row.id_customer}
                initialState={{ pagination: { paginationModel: { pageSize: 10 } } }}
                pageSizeOptions={[10, 25, 50]}
                disableRowSelectionOnClick
                sx={{ border: 'none', '& .MuiDataGrid-cell': { userSelect: 'none' } }}
            />
        </Paper>
    );
};

const IconButtonClick = ({ title, icon, color, onClick }) => (
    <Tooltip title={title}>
        <Button size="small" variant="outlined" color={color} onClick={onClick} sx={{ minWidth: '40px', p: 0.5 }}>
            {icon}
        </Button>
    </Tooltip>
);

export default CustomerTable;